'use client';

/**
 * FormWizard Component
 *
 * A multi-step form navigation component with step indicators.
 * Supports field rendering via FieldRenderer, custom step content via render props,
 * and validation before step navigation.
 */

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { NeonAccent } from '../../theme/palette';
import type { WizardStep as BaseWizardStep, FieldDefinition, WizardStepProps } from '../../types/entityFramework';
import { FieldRenderer } from '../FieldRenderer';
import { GlassButton } from '../GlassButton';
import { GlassCard } from '../GlassCard';
import { NeonCard } from '../NeonCard';
import { StepIndicator } from './StepIndicator';

// ============================================================================
// Types
// ============================================================================

/**
 * Extended WizardStep that supports both field definitions and render props
 */
export interface WizardStep extends Omit<BaseWizardStep, 'fields' | 'component'> {
  /** Field definitions for this step */
  fields?: FieldDefinition[];
  /** Custom render function for step content */
  render?: (props: WizardStepRenderProps) => React.ReactNode;
  /** Custom validation function */
  validate?: (values: Record<string, unknown>) => boolean | Promise<boolean>;
  /** Whether this step is optional */
  optional?: boolean;
}

/**
 * Props passed to custom render functions
 */
export interface WizardStepRenderProps extends WizardStepProps {
  /** Current step index */
  stepIndex: number;
  /** Total number of steps */
  totalSteps: number;
  /** Go to a specific step */
  goToStep: (step: number) => void;
  /** Neon accent color */
  neonAccent?: NeonAccent;
}

export interface FormWizardProps {
  /**
   * Array of wizard steps
   */
  steps: WizardStep[];

  /**
   * Initial form values
   */
  values?: Record<string, unknown>;

  /**
   * Callback when wizard is completed
   */
  onComplete: (values: Record<string, unknown>) => void | Promise<void>;

  /**
   * Callback when values change
   */
  onChange?: (values: Record<string, unknown>) => void;

  /**
   * Callback when step changes
   */
  onStepChange?: (step: number, direction: 'next' | 'prev') => void;

  /**
   * Callback when wizard is cancelled
   */
  onCancel?: () => void;

  /**
   * Initial step (0-indexed)
   * @default 0
   */
  initialStep?: number;

  /**
   * Whether the wizard is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the wizard is in a loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Label for the complete button
   * @default 'Complete'
   */
  completeLabel?: string;

  /**
   * Label for the next button
   * @default 'Next'
   */
  nextLabel?: string;

  /**
   * Label for the previous button
   * @default 'Previous'
   */
  prevLabel?: string;

  /**
   * Label for the cancel button
   * @default 'Cancel'
   */
  cancelLabel?: string;

  /**
   * Neon accent color
   * @default 'cyan'
   */
  neonAccent?: NeonAccent;

  /**
   * Card variant for step content
   * @default 'glass'
   */
  variant?: 'glass' | 'neon';

  /**
   * Additional class name
   */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  width: '100%',
});

const StyledStepContent = styled(motion.div)({
  width: '100%',
});

const StyledFieldGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gap: theme.spacing(2),
}));

const StyledFieldWrapper = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $width: number }>(({ $width }) => ({
  gridColumn: `span ${$width}`,
}));

const StyledActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(2),
}));

const StyledLeftActions = styled(Box)({
  display: 'flex',
  gap: 12,
});

const StyledRightActions = styled(Box)({
  display: 'flex',
  gap: 12,
});

// ============================================================================
// Animation Variants
// ============================================================================

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -50 : 50,
    opacity: 0,
  }),
};

// ============================================================================
// Component
// ============================================================================

const EMPTY_VALUES: Record<string, unknown> = {};

export const FormWizard = forwardRef<HTMLDivElement, FormWizardProps>(
  (
    {
      steps,
      values: propValues,
      onComplete,
      onChange,
      onStepChange,
      onCancel,
      initialStep = 0,
      disabled = false,
      loading = false,
      completeLabel = 'Complete',
      nextLabel = 'Next',
      prevLabel = 'Previous',
      cancelLabel = 'Cancel',
      neonAccent = 'cyan',
      variant = 'glass',
      className,
    },
    ref,
  ) => {
    const { t } = useI18n();
    // Stabilize initialValues reference to prevent infinite re-render loop.
    // Using `= {}` as a default parameter creates a new object on every render,
    // which triggers the setValues useEffect, which re-renders, ad infinitum.
    const initialValues = propValues ?? EMPTY_VALUES;

    // State
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [values, setValues] = useState<Record<string, unknown>>(initialValues);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [direction, setDirection] = useState(0);
    const [isValidating, setIsValidating] = useState(false);

    // Current step data
    const step = steps[currentStep];
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === steps.length - 1;

    // Sync initial values
    useEffect(() => {
      setValues(initialValues);
    }, [initialValues]);

    // Field change handler
    const handleFieldChange = useCallback(
      (fieldName: string, value: unknown) => {
        setValues((prev) => {
          const next = { ...prev, [fieldName]: value };
          onChange?.(next);
          return next;
        });
        // Clear error when field changes
        setErrors((prev) => {
          if (prev[fieldName]) {
            const next = { ...prev };
            delete next[fieldName];
            return next;
          }
          return prev;
        });
      },
      [onChange],
    );

    // Validate current step fields
    const validateCurrentStep = useCallback(async (): Promise<boolean> => {
      if (!step) return true;

      // Required field validation
      if (step.fields) {
        const newErrors: Record<string, string> = {};

        for (const field of step.fields) {
          if (field.required) {
            const value = values[field.name];
            const isEmpty =
              value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

            if (isEmpty) {
              newErrors[field.name] = `${field.label} is required`;
            }
          }
        }

        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          return false;
        }
      }

      // Custom validation
      if (step.validate) {
        setIsValidating(true);
        try {
          const result = await step.validate(values);
          if (!result) {
            setIsValidating(false);
            return false;
          }
        } catch {
          setIsValidating(false);
          return false;
        }
        setIsValidating(false);
      }

      return true;
    }, [step, values]);

    // Navigate to next step
    const handleNext = useCallback(async () => {
      if (disabled || loading || isValidating) return;

      const isValid = await validateCurrentStep();
      if (!isValid) return;

      if (isLastStep) {
        onComplete(values);
      } else {
        setDirection(1);
        setCurrentStep((prev) => {
          const next = prev + 1;
          onStepChange?.(next, 'next');
          return next;
        });
        setErrors({});
      }
    }, [disabled, loading, isValidating, isLastStep, validateCurrentStep, values, onComplete, onStepChange]);

    // Navigate to previous step
    const handlePrev = useCallback(() => {
      if (disabled || loading || isFirstStep) return;

      setDirection(-1);
      setCurrentStep((prev) => {
        const next = prev - 1;
        onStepChange?.(next, 'prev');
        return next;
      });
      setErrors({});
    }, [disabled, loading, isFirstStep, onStepChange]);

    // Go to a specific step
    const goToStep = useCallback(
      async (stepIndex: number) => {
        if (disabled || loading) return;
        if (stepIndex < 0 || stepIndex >= steps.length) return;
        if (stepIndex === currentStep) return;

        // Only validate when moving forward
        if (stepIndex > currentStep) {
          const isValid = await validateCurrentStep();
          if (!isValid) return;
        }

        setDirection(stepIndex > currentStep ? 1 : -1);
        setCurrentStep(stepIndex);
        setErrors({});
        onStepChange?.(stepIndex, stepIndex > currentStep ? 'next' : 'prev');
      },
      [disabled, loading, steps.length, currentStep, validateCurrentStep, onStepChange],
    );

    // Step indicator steps
    const indicatorSteps = useMemo(
      () =>
        steps.map((s) => ({
          id: s.id,
          title: s.title,
          description: s.description,
          optional: s.optional,
        })),
      [steps],
    );

    // Wizard step props for render function
    const wizardStepProps: WizardStepRenderProps = useMemo(
      () => ({
        values,
        errors,
        onChange: handleFieldChange,
        onNext: handleNext,
        onPrev: handlePrev,
        isFirst: isFirstStep,
        isLast: isLastStep,
        stepIndex: currentStep,
        totalSteps: steps.length,
        goToStep,
        neonAccent,
      }),
      [
        values,
        errors,
        handleFieldChange,
        handleNext,
        handlePrev,
        isFirstStep,
        isLastStep,
        currentStep,
        steps.length,
        goToStep,
        neonAccent,
      ],
    );

    // Render step content
    const renderStepContent = () => {
      if (!step) return null;

      // Custom render function
      if (step.render) {
        return step.render(wizardStepProps);
      }

      // Render fields using FieldRenderer
      if (step.fields && step.fields.length > 0) {
        return (
          <StyledFieldGrid>
            {step.fields.map((field) => (
              <StyledFieldWrapper key={field.name} $width={field.width || 12}>
                <FieldRenderer
                  field={field}
                  value={values[field.name]}
                  mode="edit"
                  onChange={(value) => handleFieldChange(field.name, value)}
                  error={errors[field.name]}
                  disabled={disabled || loading}
                  neonAccent={neonAccent}
                />
              </StyledFieldWrapper>
            ))}
          </StyledFieldGrid>
        );
      }

      return (
        <Typography
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            py: 4,
          }}
        >
          {t('common.noContentForStep')}
        </Typography>
      );
    };

    // Card component based on variant
    const CardComponent = variant === 'neon' ? NeonCard : GlassCard;
    const cardProps = variant === 'neon' ? { accent: neonAccent } : { neonAccent, glowOnHover: false };

    return (
      <StyledContainer ref={ref} className={className}>
        {/* Step Indicator */}
        <StepIndicator
          steps={indicatorSteps}
          currentStep={currentStep}
          onStepClick={goToStep}
          clickable={!disabled && !loading}
          neonAccent={neonAccent}
        />

        {/* Step Content */}
        <CardComponent {...cardProps}>
          {/* Step Header */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: step?.description ? 0.5 : 0,
              }}
            >
              {step?.title}
            </Typography>
            {step?.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {step.description}
              </Typography>
            )}
          </Box>

          {/* Animated Step Content */}
          <AnimatePresence mode="wait" custom={direction}>
            <StyledStepContent
              key={currentStep}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
            >
              {renderStepContent()}
            </StyledStepContent>
          </AnimatePresence>

          {/* Actions */}
          <StyledActions>
            <StyledLeftActions>
              {onCancel && (
                <GlassButton variant="ghost" color="secondary" onClick={onCancel} disabled={disabled || loading}>
                  {cancelLabel}
                </GlassButton>
              )}
            </StyledLeftActions>

            <StyledRightActions>
              {!isFirstStep && (
                <GlassButton variant="outlined" color="secondary" onClick={handlePrev} disabled={disabled || loading}>
                  {prevLabel}
                </GlassButton>
              )}

              <GlassButton
                variant="gradient"
                onClick={handleNext}
                disabled={disabled}
                loading={loading || isValidating}
              >
                {isLastStep ? completeLabel : nextLabel}
              </GlassButton>
            </StyledRightActions>
          </StyledActions>
        </CardComponent>
      </StyledContainer>
    );
  },
);

FormWizard.displayName = 'FormWizard';

export default FormWizard;
